import { Platform } from 'react-native';
import SimpleStorage from '../utils/simpleStorage';
import type { BusinessLocation } from '../types/location';
import { PRIMARY_LOCATION_ID } from '../types/location';
import { UnifiedAuthService } from './unifiedAuthService';

const STORAGE_KEY_PREFIX = 'business_locations_';

function nowIso() {
  return new Date().toISOString();
}

function defaultPrimaryLocation(businessId: string): BusinessLocation {
  return {
    id: PRIMARY_LOCATION_ID,
    businessId,
    name: 'Primary',
    isActive: true,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
}

class LocationService {
  async getLocations(businessId: string): Promise<BusinessLocation[]> {
    const key = `${STORAGE_KEY_PREFIX}${businessId}`;
    let locations: BusinessLocation[] = [];

    try {
      const local = await SimpleStorage.getItem(key);
      if (local) locations = JSON.parse(local);
    } catch {
      // ignore
    }

    // Ensure we always have a primary location for backward compatibility
    if (!locations.find((l) => l.id === PRIMARY_LOCATION_ID)) {
      locations = [defaultPrimaryLocation(businessId), ...locations];
      await SimpleStorage.setItem(key, JSON.stringify(locations));
    }

    // Firestore sync best-effort
    try {
      if (Platform.OS === 'web') {
        const { collection, getDocs, doc } = await import('firebase/firestore');
        const db = (UnifiedAuthService as any).getWebDb?.() ?? (await import('./unifiedAuthService')).getWebDb?.();
        if (db) {
          const snap = await getDocs(collection(doc(db, 'businesses', businessId), 'locations'));
          const firestoreLocations = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as BusinessLocation[];
          if (firestoreLocations.length > 0) {
            locations = firestoreLocations;
            // Ensure primary exists even if missing in Firestore
            if (!locations.find((l) => l.id === PRIMARY_LOCATION_ID)) {
              locations = [defaultPrimaryLocation(businessId), ...locations];
            }
            await SimpleStorage.setItem(key, JSON.stringify(locations));
          }
        }
      } else {
        const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
        initializeFirebase?.();
        const getFs = getMobileFirestore?.();
        if (typeof getFs === 'function') {
          const db = getFs();
          const snap = await db.collection('businesses').doc(businessId).collection('locations').get();
          const firestoreLocations = snap.docs.map((d: any) => ({ id: d.id, ...d.data() })) as BusinessLocation[];
          if (firestoreLocations.length > 0) {
            locations = firestoreLocations;
            if (!locations.find((l) => l.id === PRIMARY_LOCATION_ID)) {
              locations = [defaultPrimaryLocation(businessId), ...locations];
            }
            await SimpleStorage.setItem(key, JSON.stringify(locations));
          }
        }
      }
    } catch {
      // ignore
    }

    return locations.filter((l) => l.isActive !== false);
  }

  async saveLocations(businessId: string, locations: BusinessLocation[]): Promise<void> {
    const key = `${STORAGE_KEY_PREFIX}${businessId}`;
    await SimpleStorage.setItem(key, JSON.stringify(locations));

    try {
      if (Platform.OS === 'web') {
        const { doc, setDoc } = await import('firebase/firestore');
        const { getWebDb } = await import('./unifiedAuthService');
        const db = getWebDb();
        if (!db) return;
        for (const loc of locations) {
          await setDoc(doc(db, 'businesses', businessId, 'locations', loc.id), {
            ...loc,
            businessId,
            updatedAt: nowIso(),
          });
        }
      } else {
        const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
        initializeFirebase?.();
        const getFs = getMobileFirestore?.();
        if (typeof getFs !== 'function') return;
        const db = getFs();
        const batch = db.batch?.();
        if (batch) {
          locations.forEach((loc) => {
            const ref = db.collection('businesses').doc(businessId).collection('locations').doc(loc.id);
            batch.set(ref, { ...loc, businessId, updatedAt: nowIso() }, { merge: true });
          });
          await batch.commit();
        } else {
          for (const loc of locations) {
            await db.collection('businesses').doc(businessId).collection('locations').doc(loc.id).set(
              { ...loc, businessId, updatedAt: nowIso() },
              { merge: true }
            );
          }
        }
      }
    } catch {
      // best effort
    }
  }

  async addLocation(businessId: string, data: Omit<BusinessLocation, 'id' | 'businessId' | 'createdAt' | 'updatedAt'>): Promise<BusinessLocation> {
    const existing = await this.getLocations(businessId);
    const loc: BusinessLocation = {
      id: `loc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      businessId,
      name: data.name,
      address: data.address,
      phone: data.phone,
      timezone: data.timezone,
      hours: data.hours,
      isActive: data.isActive ?? true,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    await this.saveLocations(businessId, [...existing, loc]);
    return loc;
  }
}

export const locationService = new LocationService();
export default locationService;

